import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Card, Button, Form, Input, message, Popconfirm,
  Table, Space, Tag, Alert, Spin, Empty, Select, DatePicker, Tooltip,
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, DeleteOutlined, GlobalOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_BASE_URL, getAuthHeaders } from '../api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;

interface ScrapeUrlRow {
  id: number;
  user_uid: string;
  url: string;
  status: 'pending' | 'scraped';
  submitted_at: string;
  scraping_started_at: string | null;
  scraping_completed_at: string | null;
  submitter_email?: string;
}

/**
 * Page for users to submit website URLs for scraping.
 * All users can submit; admins see all submissions with status.
 */
const ScrapeUrls: React.FC = () => {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [urls, setUrls] = useState<ScrapeUrlRow[]>([]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const loadUrls = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/scrape-urls`, { headers });
      const text = await res.text();
      if (!res.ok) {
        setApiError(`${res.status}: ${text}`);
        return;
      }
      const data = JSON.parse(text);
      setUrls(data.urls || []);
    } catch (e: unknown) {
      setApiError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUrls();
  }, [loadUrls]);

  const handleSubmit = async (values: { url: string }) => {
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/scrape-urls`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: values.url }),
      });
      const data = await res.json();
      if (!res.ok) {
        message.error(data?.error || 'Failed to add URL');
        return;
      }
      message.success('URL added for scraping');
      form.resetFields();
      loadUrls();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminUpdate = async (
    id: number,
    updates: { status?: 'pending' | 'scraped'; scraping_started_at?: string | null; scraping_completed_at?: string | null }
  ) => {
    try {
      const headers = await getAuthHeaders();
      const body: Record<string, unknown> = {};
      if (updates.status !== undefined) body.status = updates.status;
      if (updates.scraping_started_at !== undefined) body.scraping_started_at = updates.scraping_started_at || null;
      if (updates.scraping_completed_at !== undefined) body.scraping_completed_at = updates.scraping_completed_at || null;
      const res = await fetch(`${API_BASE_URL}/api/admin/scrape-urls/${id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        message.error(data?.error || 'Failed to update');
        return;
      }
      message.success('Status updated');
      loadUrls();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to update');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/scrape-urls/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json();
        message.error(data?.error || 'Failed to delete');
        return;
      }
      message.success('URL removed');
      loadUrls();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const columns = [
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: string, record: ScrapeUrlRow) => {
        const tag = (
          <Tag color={status === 'scraped' ? 'green' : 'orange'}>
            {status === 'scraped' ? 'Scraped' : 'Pending'}
          </Tag>
        );
        if (user?.admin) {
          return (
            <Select
              value={status}
              size="small"
              style={{ width: 120 }}
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'scraped', label: 'Scraped' },
              ]}
              onChange={(v) => handleAdminUpdate(record.id, { status: v as 'pending' | 'scraped' })}
            />
          );
        }
        return tag;
      },
    },
    ...(user?.admin
      ? [
          {
            title: 'Submitted by',
            dataIndex: 'submitter_email',
            key: 'submitter_email',
            width: 160,
            render: (email: string) => email || '—',
          },
          {
            title: 'Scraping started',
            dataIndex: 'scraping_started_at',
            key: 'scraping_started_at',
            width: 150,
            render: (at: string | null, record: ScrapeUrlRow) =>
              user?.admin ? (
                <DatePicker
                  size="small"
                  allowClear
                  value={at ? dayjs(at) : null}
                  onChange={(_, dateStr) =>
                    handleAdminUpdate(record.id, {
                      scraping_started_at: (dateStr as string) || null,
                    })
                  }
                  format="YYYY-MM-DD"
                  style={{ width: 130 }}
                />
              ) : (
                at ? new Date(at).toLocaleDateString() : '—'
              ),
          },
          {
            title: 'Scraping completed',
            dataIndex: 'scraping_completed_at',
            key: 'scraping_completed_at',
            width: 150,
            render: (at: string | null, record: ScrapeUrlRow) =>
              user?.admin ? (
                <DatePicker
                  size="small"
                  allowClear
                  value={at ? dayjs(at) : null}
                  onChange={(_, dateStr) =>
                    handleAdminUpdate(record.id, {
                      scraping_completed_at: (dateStr as string) || null,
                    })
                  }
                  format="YYYY-MM-DD"
                  style={{ width: 130 }}
                />
              ) : (
                at ? new Date(at).toLocaleDateString() : '—'
              ),
          },
        ]
      : []),
    {
      title: 'Submitted',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      width: 150,
      render: (at: string) => new Date(at).toLocaleDateString(),
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      align: 'right' as const,
      render: (_: unknown, record: ScrapeUrlRow) => (
        <Popconfirm
          title="Remove this URL?"
          description="It will no longer appear in the list."
          onConfirm={() => handleDelete(record.id)}
          okText="Remove"
          okButtonProps={{ danger: true }}
        >
          <Tooltip title="Remove">
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Title level={3}>Submit URLs for Scraping</Title>
      <Paragraph type="secondary">
        Add websites containing competition results. Your submissions are stored
        for administrators to process. {user?.admin && 'As admin, you see all submissions.'}
      </Paragraph>

      <Card
        title={<Space><GlobalOutlined /> Add Website</Space>}
        style={{ marginBottom: 24 }}
      >
        <Form form={form} layout="inline" onFinish={handleSubmit}>
          <Form.Item
            name="url"
            rules={[
              { required: true, message: 'Enter a URL' },
              {
                pattern: /^https?:\/\/.+/,
                message: 'URL must start with http:// or https://',
              },
            ]}
            style={{ flex: 1, minWidth: 240 }}
          >
            <Input
              placeholder="https://example.com/results"
              prefix={<GlobalOutlined style={{ color: 'var(--blue-bell)' }} />}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={submitting}>
              Add URL
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card
        className="scrape-submissions-card"
        title={user?.admin ? 'All submissions' : 'Your submissions'}
        extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadUrls} loading={loading} />}
        style={{ overflow: 'hidden' }}
        styles={{ body: { padding: isMobile ? 12 : 24, maxWidth: '100%', overflowX: 'auto' } }}
      >
        {apiError && (
          <Alert
            type="error"
            showIcon
            message="Error"
            closable
            description={<Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{apiError}</Text>}
            style={{ marginBottom: 16 }}
            onClose={() => setApiError(null)}
          />
        )}
        <Spin spinning={loading}>
          {urls.length === 0 && !loading && !apiError ? (
            <div>
              <Empty
                description="No URLs yet — add a website above to submit"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            <Table
              className="scrape-urls-table"
              dataSource={urls}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              size="small"
              style={{ minWidth: isMobile ? 800 : 950 }}
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default ScrapeUrls;
